import React from 'react';
import { withInjectedProps } from '../../../../Core/InjectedPropsContext';
import { useSelector } from 'react-redux';
import {
  WorksheetGridFieldSection,
  WorksheetEquipmentDropdown,
  WorksheetEquipmentStatus,
  WorksheetRecordGrid,
} from '../../../Core';
import { Field, TextOutput, NumericInput, DateInput, TimeInput } from '../../../../Core';
import deepEquals from '../../../../../Lib/Object/deepEquals';
import { IWorksheetComponent } from 'Components/Worksheets/Core/types';
import { pipe } from 'Lib/Hooks/Selectors';
import { hasNonEmptyValue, hasValue, valueOrDefault } from 'Lib/Any';
import { DateTimeDataFormat } from 'Lib/Format';

let AsCompactionDataSection: React.FC<IWorksheetComponent> = ({ createWorksheetSelector }) => {
  const minTestPortions = 4;

  const enterConvertedWetDensity =
    useSelector(createWorksheetSelector!('Preparation.EnterConvertedWetDensity')) ?? false;

  const useSameMouldForAllTestPortions =
    useSelector(createWorksheetSelector!('Equipment.UseMouldForAllTestPortions')) ?? false;

  const stabiliserUsed = useSelector(
    pipe(createWorksheetSelector!('Preparation.StabiliserUsed'), valueOrDefault(false))
  ) as boolean;

  const useSameCuringForAllTestPortions =
    useSelector(createWorksheetSelector!('Curing.CuringAppliesToSubSamples')) ?? false;

  /**
   * A collection of properties we need to select from each test portion
   * to render the record
   */
  const testPortionRecordProps = useSelector(
    pipe(createWorksheetSelector!('TestPortions'), (portions: Array<any>) =>
      (portions ?? []).map((p: any) => ({
        dim: p.ExcludedPoint,

        showMouldStatusButton:
          (hasNonEmptyValue(p.Mould?.Status) && !p.Mould.Status.InCalibration) || p.MouldCalibrationMismatch,

        lockMould: useSameMouldForAllTestPortions,
        lockMouldMass: useSameMouldForAllTestPortions || hasValue(p.Mould?.Mass),
        lockMouldVolume: useSameMouldForAllTestPortions || hasValue(p.Mould?.Volume),

        // We need to obtain the full mould for each record as the status button requires it :/
        mould: p.Mould,
        mouldMass: p.MouldMass,
        mouldVolume: p.MouldVolume,
        mouldCalibrationMismatch: p.MouldCalibrationMismatch,
      }))
    ),
    deepEquals
  );

  return (
    <WorksheetGridFieldSection name="TestPortions">
      <WorksheetRecordGrid
        name={'TestPortions'}
        orientation={'horizontal'}
        rowHeaderWidth={200}
        scrollLinkId="mddCompaction"
        recordProps={{
          width: 130,
          dim: (i) => testPortionRecordProps[i]?.dim,
          heading: 'number',
          headingLabel: 'Test Portion ID',
          preventRemove: testPortionRecordProps.length <= minTestPortions,
          removeConfirm: {
            confirm: true,
            confirmTitle: 'Portion Deletion',
            confirmText: 'Are you sure you want to delete the selected portion?',
            confirmButtonText: 'Delete Portion',
          },
        }}
        fieldSet={[
          {
            name: '',
            component: (i) => (
              <WorksheetEquipmentStatus
                equipmentType="density-mould"
                value={testPortionRecordProps[i].mould}
                warningIconOnly
                importedCalibration={{
                  Mass: testPortionRecordProps[i].mouldMass,
                  Volume: testPortionRecordProps[i].mouldVolume,
                  Mismatch: testPortionRecordProps[i].mouldCalibrationMismatch,
                }}
                importModel={{
                  Id: testPortionRecordProps[i].mould?.Id,
                }}
              />
            ),
            hidden: useSameMouldForAllTestPortions || !testPortionRecordProps.some((p) => p.showMouldStatusButton),
          },
          {
            name: 'Mould',
            component: (i) =>
              testPortionRecordProps[i].lockMould ? (
                <Field input={<TextOutput />} value={testPortionRecordProps[i].mould?.Code} />
              ) : (
                <Field
                  input={<WorksheetEquipmentDropdown />}
                  equipmentType="density-mould"
                  placeholder="Select..."
                  disabled={useSameMouldForAllTestPortions}
                />
              ),
          },
          {
            name: 'MouldMass',
            component: (i) => (
              <Field input={testPortionRecordProps[i].lockMouldMass ? <TextOutput /> : <NumericInput />} />
            ),
          },
          {
            name: 'MouldVolume',
            component: (i) => (
              <Field input={testPortionRecordProps[i].lockMouldVolume ? <TextOutput /> : <NumericInput />} />
            ),
          },
          { name: 'PortionMass', component: <Field input={<NumericInput />} />, hidden: !enterConvertedWetDensity },
          {
            name: 'WaterAddedTargetPercent',
            component: <Field input={<NumericInput />} />,
            hidden: !enterConvertedWetDensity,
          },
          { name: 'WaterAddedTarget', component: <Field input={<TextOutput />} />, hidden: !enterConvertedWetDensity },
          {
            name: 'PortionMassAfterWaterAdded',
            component: <Field input={<NumericInput />} />,
            hidden: !enterConvertedWetDensity,
          },
          { name: 'WaterAdded', component: <Field input={<TextOutput />} />, hidden: !enterConvertedWetDensity },
          { name: 'WaterAddedPercent', component: <Field input={<TextOutput />} />, hidden: !enterConvertedWetDensity },
          {
            name: 'Curing.StartedAtDateTime',
            component: <Field input={<DateInput />} dataFormat={DateTimeDataFormat.UnspecifiedLocalDateTime} />,
            hidden: useSameCuringForAllTestPortions,
            label: 'Curing Start Date',
          },
          {
            name: 'Curing.StartedAtDateTime',
            component: <Field input={<TimeInput />} dataFormat={DateTimeDataFormat.UnspecifiedLocalDateTime} />,
            hidden: useSameCuringForAllTestPortions,
            label: 'Curing Start Time',
          },
          {
            name: 'Curing.CompletedAtDateTime',
            component: <Field input={<DateInput />} dataFormat={DateTimeDataFormat.UnspecifiedLocalDateTime} />,
            hidden: useSameCuringForAllTestPortions,
            label: 'Curing Finish Date',
          },
          {
            name: 'Curing.CompletedAtDateTime',
            component: <Field input={<TimeInput />} dataFormat={DateTimeDataFormat.UnspecifiedLocalDateTime} />,
            hidden: useSameCuringForAllTestPortions,
            label: 'Curing Finish Time',
          },
          {
            name: 'Curing.Duration',
            component: <Field input={<TextOutput />} />,
            hidden: useSameCuringForAllTestPortions,
          },
          {
            name: 'Curing.TimeBetweenAdditionOfStabiliserAndCompaction',
            component: <Field input={<TextOutput />} />,
            hidden: useSameCuringForAllTestPortions || !stabiliserUsed,
            label: 'Addn. Stab. to Compaction (h)', // Abbreviated
          },
          { name: 'WetSoilAndMouldMass', component: <Field input={<NumericInput />} /> },
          { name: 'WetSoilMass', component: <Field input={<TextOutput />} /> },
          { name: 'WetDensity', component: <Field input={<TextOutput />} /> },
        ]}
      />
    </WorksheetGridFieldSection>
  );
};

AsCompactionDataSection = withInjectedProps(AsCompactionDataSection);

export { AsCompactionDataSection };
export default AsCompactionDataSection;
